import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { memoize } from "lodash";
import { useMemo } from "react";
import { debugLog, errorLog } from "../../../logger";
import getAccountGroups, { queryKey, } from "../requests/getAccountGroups";
import { cacheTimeMs, staleTimeMs } from "../Shared";
import { createDateRangesBetween } from "../utilities/getAccountGroups";
import { GroupNotFoundError } from "./Types";
import useGetDatesForTimeFrame from "./useGetDatesForTimeFrame";
function changeDateRange(requestBody, dateRange) {
    return {
        ...requestBody,
        dateAndReturnSettings: {
            ...requestBody.dateAndReturnSettings,
            dateRanges: dateRange,
        },
    };
}
function getGroupFromResponse(groupId, groups = []) {
    return groups.find((g) => g.accountGroupId === groupId);
}
export const memoizedGetGroupFromResponse = memoize((_timeFrame, _customTimeFrame, groupId, groups) => getGroupFromResponse(groupId, groups), (timeFrame, customTimeFrame, groupId, data) => {
    // only memoize in 1 hour chunks (this is `HH`) ex: https://date-fns.org/v2.29.3/docs/format
    const now = format(new Date(), "P:HH");
    const customRange = customTimeFrame
        ? `-${customTimeFrame.startDate}-${customTimeFrame.endDate}`
        : "";
    return `${now}-${timeFrame}${customRange}-${groupId}-${data?.length}`;
});
export const memoizedCreateDateRangesBetween = memoize((startDate, endDate) => createDateRangesBetween(startDate, endDate), (startDate, endDate) => `${startDate}-${endDate}`);
export default function useGetAccountGroupsForTimeFrame(groupId, timeFrame, customTimeFrame, personId, authConfig) {
    const requestBody = useMemo(() => ({
        personId: personId ?? "null",
        dateAndReturnSettings: {
            dateRanges: null,
            feeMethod: "grossOfFees",
            returnMethod: "cumulative",
        },
    }), [personId]);
    const timeFrameDates = useGetDatesForTimeFrame(groupId, timeFrame, customTimeFrame, personId, authConfig);
    // Taking our start and end date, create an array of every day pair in the range. The server only reports
    // balance data for the `endDate` so we need an array with every possible date as an `endDate` so we can
    // draw the line chart.
    const dateRangeArray = timeFrameDates.data === undefined
        ? null
        : memoizedCreateDateRangesBetween(timeFrameDates.data.startDate, timeFrameDates.data.endDate);
    const fullRequestBody = useMemo(() => changeDateRange(requestBody, dateRangeArray), [dateRangeArray, requestBody]);
    const requestEnabled = dateRangeArray !== null &&
        authConfig !== null &&
        groupId !== null &&
        personId !== null;
    const query = useQuery({
        queryKey: groupId === null
            ? undefined
            : queryKey(fullRequestBody, timeFrame, groupId),
        queryFn: async () => authConfig === null || groupId === null
            ? undefined
            : getAccountGroups(fullRequestBody, authConfig),
        enabled: requestEnabled,
        cacheTime: cacheTimeMs,
        staleTime: staleTimeMs,
    });
    const result = useMemo(() => {
        if (timeFrameDates.isLoading || timeFrameDates.hasError) {
            return timeFrameDates;
        }
        if (query.data === undefined && query.isError) {
            debugLog("useGetAccountGroupsForTimeFrame", `useQuery error with ${timeFrame} for ${groupId} - ${query.error}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: query.error,
            };
        }
        if (query.data === undefined && query.isLoading) {
            return { data: undefined, isLoading: true, hasError: false };
        }
        if (groupId === null) {
            return { data: undefined, isLoading: true, hasError: false };
        }
        if (query.data === undefined) {
            debugLog("useGetAccountGroupsForTimeFrame", `data is undefined but loading and error are false with ${timeFrame} for ${groupId}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new Error("Unknown error"),
            };
        }
        const accountGroup = memoizedGetGroupFromResponse(timeFrame, customTimeFrame, groupId, query.data);
        if (accountGroup === undefined) {
            errorLog("useGetAccountGroupsForTimeFrame", `Group "${groupId}" not found for ${timeFrame}`);
            return {
                data: undefined,
                isLoading: false,
                hasError: true,
                error: new GroupNotFoundError(groupId),
            };
        }
        return {
            data: accountGroup,
            isLoading: false,
            hasError: false,
        };
    }, [
        groupId,
        query.data,
        query.error,
        query.isError,
        query.isLoading,
        timeFrame,
        customTimeFrame,
        timeFrameDates,
    ]);
    return result;
}
//# sourceMappingURL=useGetAccountGroupForTimeFrame.js.map